import React from "react";
import Input from "common/components/Input";
import { Stack, Typography, Avatar, Icon } from "@mui/material";
import ColorPicker from "common/components/ColorPicker";
import useFetcher from "common/hooks/useFetcher";
import validate from "constants/validate";
import ActionButton from "common/components/ActionButton";
import EditorTable from "common/components/Table/editor";
import toast from "react-hot-toast";
import { theme } from "theme";
import FileUpload from "common/components/FileUpload";
export default function CreatePlatform({ refresh, onClose }) {
  const [name, setName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [imageFile, setImageFile] = React.useState(null);
  const [configs, setConfigs] = React.useState([
    ["property"],
    { property: "id" },
    { property: "alias" },
  ]);
  const [configFile, setConfigFile] = React.useState(null);
  const [geos, setGeos] = React.useState([["name", "code"]]);
  const [geoFile, setGeoFile] = React.useState(null);
  // const [platformConfig, setPlatformConfig] = React.useState("");
  const [description, setDescription] = React.useState("");
  const fetcher = useFetcher();

  const createPlatform = async () => {
    let platformConfig = configs
      .slice(1)
      .reduce((a, c) => ({ ...a, [c.property]: "" }), {});
    if (
      !Object.keys(platformConfig).includes("alias") ||
      !Object.keys(platformConfig).includes("id")
    )
      return toast.error(
        "Configuration must have both an 'id' and an 'alias' property"
      );
    if (geos[0][0] !== "name" || geos[0][1] !== "code") {
      return toast.error("geo headers are incorrect");
    }
    let payload = {
      type: name,
      description,
      imageFile,
      name,
      platformConfig,
      geos: geos.slice(1),
    };
    if (!validate("add_platform", payload)) throw new Error("incorrect format");
    await fetcher.post(
      "adding platform",
      `/api/platforms`,
      () => {
        let form = new FormData();
        form.append("json", JSON.stringify(payload));
        form.append("Icon", imageFile);
        return form;
      },
      ({ id, error }, toast) => {
        if (id) {
          toast.success("Platform added!");
          refresh();
          onClose();
          return;
        }
        toast.error(error);
      },
      (e) => {}
    );
    return true;
  };
  return (
    <Stack p={2} gap={3}>
      <Stack alignItems={"center"} direction="row" gap={1}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            marginRight: "10px",
          }}
        >
          <Icon
            style={{
              fontSize: "40px",
              color: theme.palette.primary.main,
            }}
          >
            add
          </Icon>
        </Avatar>
        <Stack>
          <Typography variant="h4">Create Platform</Typography>
        </Stack>
      </Stack>
      <Stack className="floating" gap={3}>
        <Typography variant="h5">Information</Typography>
        <Stack gap={1}>
          <Typography>Define your platforms's name and description.</Typography>
        </Stack>
        <Stack gap={1}>
          <Input
            size="small"
            dark
            sx={{ width: "100%" }}
            placeholder="MyNewPlatform"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            size="small"
            dark
            sx={{ width: "100%" }}
            placeholder="Platform description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
            maxRows={3}
          />
        </Stack>
      </Stack>
      <Stack className="floating" gap={3}>
        <Typography variant="h5">Icon</Typography>
        <Stack gap={1}>
          <Typography>
            Upload a PNG file to be used as this platform's icon.
          </Typography>
        </Stack>
        <Stack gap={1} flexDirection={"row"} alignItems={"center"}>
          <FileUpload
            sizeInKb={300}
            file={imageFile}
            allowedFileTypes={["image/png"]}
            handleFileChange={(f) => {
              setImageFile(f);
            }}
          >
            Upload Icon
          </FileUpload>
          {imageFile && (
            <div
              style={{
                overflow: "hidden",
                height: "80px",
                width: "80px",
                marginLeft: "40px",
                backgroundImage: `url(${URL.createObjectURL(imageFile)})`,
                backgroundSize: "cover",
              }}
            />
          )}
        </Stack>
      </Stack>
      <Stack className="floating" gap={3}>
        <Typography variant="h5">Geography</Typography>
        <Stack gap={1}>
          <Typography>
            {`(Optional) Upload available geographies for this platform. You can upload a JSON file to populate this data. The JSON file must be an array and each object should look like this - {"name":<name>,"code":<code>}.`}
          </Typography>
        </Stack>
        <Stack sx={{ height: "450px" }} gap={1}>
          <EditorTable
            noHeader
            noColumnMod
            cellFlex
            clearButtonTitle="Clear"
            loading={loading}
            data={geos}
            close={() => {
              setGeos([["name", "code"]]);
            }}
            save={(data) => {
              setGeos(data);
            }}
          />
        </Stack>
        <Stack sx={{ paddingLeft: "10px" }} gap={1} alignItems={"start"}>
          <FileUpload
            sizeInKb={600}
            file={geoFile}
            handleFileChange={(file) => {
              setLoading(true);
              const reader = new FileReader();
              reader.onload = (e) => {
                try {
                  const result = e.target?.result;
                  if (typeof result !== "string")
                    throw new Error("Invalid file content");

                  const jsonData = JSON.parse(result);
                  let headers = Object.keys(jsonData[0]);
                  let valid = true;
                  headers.forEach((h) => {
                    if (!["name", "code"].includes(h) && valid) {
                      valid = false;
                    }
                  });
                  if (!valid) {
                    toast.error("allowed properties are name and code");
                    return;
                  }
                  const geosValue = jsonData.reduce((a, c) => [...a, c], [
                    ["name", "code"],
                  ]);
                  setGeos(geosValue);
                } catch (err) {
                  const errorMessage =
                    err instanceof Error
                      ? err.message
                      : "Unknown error occurred";
                  toast.error(errorMessage);
                }
              };
              if (file) {
                reader.readAsText(file);
              }
              setGeoFile(file);
              setLoading(false);
            }}
            hideFileName
          >
            Upload JSON
          </FileUpload>
        </Stack>
      </Stack>
      <Stack className="floating" gap={3}>
        <Typography variant="h5">Configuration Definition</Typography>
        <Stack gap={1}>
          <Typography>
            Provide a list of properties that should be in each tenant JSON
            configuration for this platform. Both 'id' and 'alias' must be part
            of the tenant configuration.
          </Typography>
        </Stack>
        <Stack sx={{ height: "450px" }} gap={1}>
          <EditorTable
            noHeader
            noColumnMod
            cellFlex
            clearButtonTitle="Clear"
            loading={loading}
            data={configs}
            close={() => {
              setConfigs([
                ["property"],
                { property: "id" },
                { property: "alias" },
              ]);
            }}
            save={(data) => {
              setConfigs(data);
            }}
          />
        </Stack>
        <Stack sx={{ paddingLeft: "10px" }} gap={1} alignItems={"start"}>
          <FileUpload
            sizeInKb={600}
            file={configFile}
            handleFileChange={(file) => {
              setLoading(true);
              const reader = new FileReader();
              reader.onload = (e) => {
                try {
                  const result = e.target?.result;
                  if (typeof result !== "string")
                    throw new Error("Invalid file content");

                  const jsonData = JSON.parse(result);
                  let valid = false;
                  if (
                    typeof jsonData === "object" &&
                    !Array.isArray(jsonData) &&
                    Object.keys(jsonData)?.length > 0
                  ) {
                    valid = true;
                  }
                  if (!valid) {
                    return toast.error("uploaded config is not valid");
                  }

                  const configsValue = Object.keys(jsonData).reduce(
                    (a, c) => [...a, { property: c }],
                    [["property"]]
                  );
                  setConfigs(configsValue);
                } catch (err) {
                  const errorMessage =
                    err instanceof Error
                      ? err.message
                      : "Unknown error occurred";
                  toast.error(errorMessage);
                }
              };
              if (file) {
                reader.readAsText(file);
              }
              setConfigFile(file);
              setLoading(false);
            }}
            hideFileName
          >
            Upload JSON
          </FileUpload>
        </Stack>
        {/* <Stack gap={1}>
          <Input
            size="small"
            dark
            sx={{ width: "100%" }}
            placeholder="Platform configuration"
            value={platformConfig}
            onChange={(e) => setPlatformConfig(e.target.value)}
            multiline
            minRows={5}
            maxRows={5}
          />
        </Stack> */}
      </Stack>
      <div style={{ marginLeft: "auto" }}>
        <ActionButton
          icon="save"
          dark
          handler={async () => {
            await createPlatform();
          }}
        >
          Save Platform
        </ActionButton>
      </div>
    </Stack>
  );
}
